/**
 * TanStack Query hooks for the `/api/admin/*` endpoints.
 *
 * Every hook uses the shared `apiGet` helper so the 401 interceptor
 * and credential handling stay centralized. Query keys are structured
 * hierarchically so individual pages can invalidate a whole slice
 * (e.g. `['admin', 'login-analytics']`).
 *
 * Admin endpoints are per-user / sensitive — they map to the
 * `ADMIN_ANALYTICS` cache key (`Cache-Control: private, max-age=0,
 * s-maxage=0` on the backend). React Query keeps the in-memory result for
 * `gcTime` but always refetches on mount.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type {
  ActivityDailyPoint,
  ActivitySummary,
  ActivityTopCountry,
  ActivityTopDocument,
  ActivityTopEvent,
  ActivityTopPage,
  ActivityUserDetail,
  ActivityUsersResponse,
  ActivityUsersSortKey,
  LoginDailyPoint,
  LoginSummary,
  LoginUserDetail,
  LoginUsersResponse,
  LoginUsersSortKey,
  SortOrder,
} from './types';

// Login analytics

export function useLoginSummary(): UseQueryResult<LoginSummary> {
  return useQuery<LoginSummary>({
    queryKey: ['admin', 'login-analytics', 'summary'],
    queryFn: () => apiGet<LoginSummary>(endpoints.admin.loginAnalytics.summary()),
  });
}

export function useLoginDaily(days: number): UseQueryResult<LoginDailyPoint[]> {
  return useQuery<LoginDailyPoint[]>({
    queryKey: ['admin', 'login-analytics', 'daily', days],
    queryFn: () => apiGet<LoginDailyPoint[]>(endpoints.admin.loginAnalytics.daily({ days })),
  });
}

export interface LoginUsersParams {
  sortBy: LoginUsersSortKey;
  order: SortOrder;
  search: string;
  limit: number;
  offset: number;
}

export function useLoginUsers(params: LoginUsersParams): UseQueryResult<LoginUsersResponse> {
  const { sortBy, order, search, limit, offset } = params;
  return useQuery<LoginUsersResponse>({
    queryKey: ['admin', 'login-analytics', 'users', sortBy, order, search, limit, offset],
    queryFn: () =>
      apiGet<LoginUsersResponse>(
        endpoints.admin.loginAnalytics.users({
          sort_by: sortBy,
          order,
          limit,
          offset,
          // Only emit `search` when non-empty.
          search: search || undefined,
        }),
      ),
    placeholderData: (prev) => prev,
  });
}

export function useLoginUserDetail(email: string | null): UseQueryResult<LoginUserDetail> {
  return useQuery<LoginUserDetail>({
    queryKey: ['admin', 'login-analytics', 'user', email],
    enabled: !!email,
    queryFn: () => apiGet<LoginUserDetail>(endpoints.admin.loginAnalytics.user(email ?? '')),
  });
}

// User activity

export function useActivitySummary(): UseQueryResult<ActivitySummary> {
  return useQuery<ActivitySummary>({
    queryKey: ['admin', 'user-activity', 'summary'],
    queryFn: () => apiGet<ActivitySummary>(endpoints.admin.activity.summary()),
  });
}

export function useActivityDaily(days: number): UseQueryResult<ActivityDailyPoint[]> {
  return useQuery<ActivityDailyPoint[]>({
    queryKey: ['admin', 'user-activity', 'daily', days],
    queryFn: () => apiGet<ActivityDailyPoint[]>(endpoints.admin.activity.daily({ days })),
  });
}

export function useActivityTopPages(days: number, limit = 10): UseQueryResult<ActivityTopPage[]> {
  return useQuery<ActivityTopPage[]>({
    queryKey: ['admin', 'user-activity', 'top-pages', days, limit],
    queryFn: () =>
      apiGet<ActivityTopPage[]>(
        endpoints.admin.activity.top({ dimension: 'pages', days, limit }),
      ),
  });
}

export function useActivityTopEvents(days: number, limit = 10): UseQueryResult<ActivityTopEvent[]> {
  return useQuery<ActivityTopEvent[]>({
    queryKey: ['admin', 'user-activity', 'top-events', days, limit],
    queryFn: () =>
      apiGet<ActivityTopEvent[]>(
        endpoints.admin.activity.top({ dimension: 'events', days, limit }),
      ),
  });
}

export function useActivityTopDocuments(
  days: number,
  limit = 10,
): UseQueryResult<ActivityTopDocument[]> {
  return useQuery<ActivityTopDocument[]>({
    queryKey: ['admin', 'user-activity', 'top-documents', days, limit],
    queryFn: () =>
      apiGet<ActivityTopDocument[]>(
        endpoints.admin.activity.top({ dimension: 'documents', days, limit }),
      ),
  });
}

export function useActivityTopCountries(
  days: number,
  limit = 10,
): UseQueryResult<ActivityTopCountry[]> {
  return useQuery<ActivityTopCountry[]>({
    queryKey: ['admin', 'user-activity', 'top-countries', days, limit],
    queryFn: () =>
      apiGet<ActivityTopCountry[]>(
        endpoints.admin.activity.top({ dimension: 'countries', days, limit }),
      ),
  });
}

export interface ActivityUsersParams {
  sortBy: ActivityUsersSortKey;
  order: SortOrder;
  search: string;
  limit: number;
  offset: number;
}

export function useActivityUsers(
  params: ActivityUsersParams,
): UseQueryResult<ActivityUsersResponse> {
  const { sortBy, order, search, limit, offset } = params;
  return useQuery<ActivityUsersResponse>({
    queryKey: ['admin', 'user-activity', 'users', sortBy, order, search, limit, offset],
    queryFn: () =>
      apiGet<ActivityUsersResponse>(
        endpoints.admin.activity.users({
          sort_by: sortBy,
          order,
          limit,
          offset,
          // Only emit `search` when non-empty.
          search: search || undefined,
        }),
      ),
    placeholderData: (prev) => prev,
  });
}

export function useActivityUserDetail(email: string | null): UseQueryResult<ActivityUserDetail> {
  return useQuery<ActivityUserDetail>({
    queryKey: ['admin', 'user-activity', 'user', email],
    enabled: !!email,
    queryFn: () => apiGet<ActivityUserDetail>(endpoints.admin.activity.user(email ?? '')),
  });
}
